// src/pages/LoginPage.tsx (or your main auth page file)
import React, { useState, useContext } from 'react';
import { AuthContext } from '../store/ContextStore';
import { useChatHistory } from '../store/ChatHistoryContext';
import { useNavigate, Outlet } from 'react-router-dom';
import LoginForm from '../components/LoginForm';
import CreateUserForm from '../components/CreateUserForm';
import ForgotPasswordForm from '../components/ForgotPasswordForm';
import IssueReportForm from '../components/IssueReportForm';
import RecordbookUploadNoticeModal from '../components/modals/RecordbookUploadNoticeModal';
import UploadNotificationModal, { UploadNotification } from '../components/modals/UploadNotificationModal';
import localStorageService from '../services/localStorageService';
import AuthService from '../services/authService';
import { normalizeUserFromSubscription } from '../services/trialService';
import { TEAM_SEAT_OVERFLOW } from '../constants/routes';
import eventEmitter from '../utils/eventEmitter';

// Define Sport interface here since it's no longer imported from CreateUserForm
interface Sport {
  name: string;
  code: string;
  gLeagueId: number;
}

interface LoginErrorState {
    Username?: string;
    Password?: string;
    General?: string;
}

interface CreateUserErrorState {
    UserName?: string;
    Email?: string;
    Password?: string;
    ConfirmPassword?: string;
    TeamName?: string;
    TeamCode?: string;
    Sports?: string; // For general sports section errors
    CurrentSport?: string; // For errors related to the sport currently being added
    General?: string;
}

interface TeamPayload {
    code: number | null;
    name: string;
    gTeamId?: number;
    tidyName?: string;
    imgUrl?: string;
    colorCode?: string;
}

export default function Login() {
    // View state: 'login', 'register', or 'forgotPassword'
    const [currentView, setCurrentView] = useState('login');
    const [isPending, setIsPending] = useState(false);
    const [issueDialogOpen, setIssueDialogOpen] = useState(false);
    const [isRecordbookModalOpen, setIsRecordbookModalOpen] = useState(false);
    const [recordbookTeamName, setRecordbookTeamName] = useState<string | undefined>(undefined);
    const [recordbookMissing, setRecordbookMissing] = useState<Record<string, unknown> | null>(null);

    const [uploadNotifications, setUploadNotifications] = useState<UploadNotification[]>([]);
    const [isUploadNotificationOpen, setIsUploadNotificationOpen] = useState(false);

    const [loginUsername, setLoginUsername] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginError, setLoginError] = useState<LoginErrorState>({});
    const [showLoginPassword, setShowLoginPassword] = useState(false);

    const [createUsername, setCreateUsername] = useState('');
    const [createEmail, setCreateEmail] = useState('');
    const [createPassword, setCreatePassword] = useState('');
    const [createConfirmPassword, setCreateConfirmPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [createTeamName, setCreateTeamName] = useState('');
    const [createTeamCode, setCreateTeamCode] = useState('');
    
    const [createSportsList, setCreateSportsList] = useState<Sport[]>([]);
    const [currentSportName, setCurrentSportName] = useState('');
    const [currentSportCode, setCurrentSportCode] = useState('');
    const [currentSportGLeagueId, setCurrentSportGLeagueId] = useState('');

    const [createError, setCreateError] = useState<CreateUserErrorState>({});
    const [showCreatePassword, setShowCreatePassword] = useState(false);
    const [showCreateConfirmPassword, setShowCreateConfirmPassword] = useState(false);

    const navigate = useNavigate();
    const { setUser, setIsAuthenticated, setSelectedSport, setTeam: setAuthTeam } = useContext(AuthContext);
    const { clearHistory } = useChatHistory();
    const authService = new AuthService();

    const handleLogin = async () => {
        clearHistory();
        localStorage.clear();
        setLoginError({});
        setIsPending(true);

        if (!loginUsername.trim() || !loginPassword.trim()) {
            setLoginError({ General: "Username and Password are required."});
            if (!loginUsername.trim()) setLoginError(prev => ({...prev, Username: "Username required"}));
            if (!loginPassword.trim()) setLoginError(prev => ({...prev, Password: "Password required"}));
            setIsPending(false);
            return;
        }
        
        const payload = new URLSearchParams();
        payload.append('username', loginUsername);
        payload.append('password', loginPassword);

        const response = await authService.UserLogin(payload);
        setIsPending(false);

        if (response && response.status === 200) {
            // Check if team seat limit is reached from login response
            const teamSeatInfo = response.data?.team_seat_info;

            const userData = normalizeUserFromSubscription(response.data?.user || response.data);

            // Include team_seat_info in userData for localStorage
            if (teamSeatInfo) {
                userData.team_seat_info = teamSeatInfo;
            }

            // ALWAYS set auth context and navigate - let dashboard handle terms modal
            localStorageService.addUserInfo(userData);
            const sportCodes: string[] = Array.isArray(userData?.sports) ? userData.sports.map((s: any) => s?.code).filter(Boolean) : [];
            const sportCode = sportCodes.includes('MFB') ? 'MFB' : (sportCodes[0] || null);
            if (sportCode) localStorageService.setSelectedSport(sportCode);
            
            setUser(userData);
            setIsAuthenticated(true);
            if (sportCode) setSelectedSport(sportCode);
            setAuthTeam(userData.team || null);

            try {
                const notificationsResponse = await authService.getUploadNotifications();
                if (notificationsResponse?.status === 200 && notificationsResponse?.data?.notifications?.length > 0) {
                    setUploadNotifications(notificationsResponse.data.notifications);
                    setIsUploadNotificationOpen(true);
                }
            } catch (error) {
                console.error("Error fetching upload notifications:", error);
            }

            // Check if team seat limit is reached - either from team_seat_info OR from sport_access status
            const subscription = (userData as any)?.subscription;
            const sportAccess = subscription?.sport_access ?? subscription?.sportAccess;
            const hasSeatOverflow = teamSeatInfo?.limit_reached ||
                (sportCode && sportAccess?.[sportCode]?.status === "seat_locked");

            // Navigate to team seat overflow page if limit is reached, otherwise go to dashboard
            if (hasSeatOverflow) {
                eventEmitter.emit("team_seat_overflow", {
                    code: "TEAM_SEAT_OVERFLOW",
                    message: teamSeatInfo?.message || "Access is currently unavailable because your team has reached the maximum number of allowed users. Please contact your team administrator to request access.",
                });
                navigate(TEAM_SEAT_OVERFLOW, { replace: true });
            } else {
                navigate("/dashboard");
            }
        } else {
            const errorMsg = response?.data?.message || response?.data?.detail || response?.message || "Invalid username or password.";
            setLoginError({ General: errorMsg });
        }
    };

    const handleAddSportToList = () => {
        if (!currentSportName.trim() || !currentSportCode.trim() || !currentSportGLeagueId.trim()) {
            setCreateError(prev => ({ ...prev, CurrentSport: "All sport fields (Name, Code, gLeagueId) are required to add a sport." }));
            return;
        }
        const gLeagueIdNum = parseInt(currentSportGLeagueId, 10);
        if (isNaN(gLeagueIdNum)) {
            setCreateError(prev => ({ ...prev, CurrentSport: "gLeagueId must be a valid number." }));
            return;
        }

        setCreateSportsList(prevList => [
            ...prevList,
            { name: currentSportName.trim(), code: currentSportCode.trim(), gLeagueId: gLeagueIdNum }
        ]);
        setCurrentSportName('');
        setCurrentSportCode('');
        setCurrentSportGLeagueId('');
        setCreateError(prev => ({ ...prev, CurrentSport: "", Sports: "" })); // Clear current sport error and general sports error
    };

    const handleRemoveSportFromList = (indexToRemove: number) => {
        setCreateSportsList(prevList => prevList.filter((_, index) => index !== indexToRemove));
    };

    const validateCreateUser = (): boolean => {
        const errors: CreateUserErrorState = {};
        if (!createUsername.trim()) errors.UserName = "Username is required.";
        if (!createEmail.trim()) {
            errors.Email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(createEmail)) {
            errors.Email = "Email is invalid.";
        }
        if (!createPassword) {
            errors.Password = "Password is required.";
        } else if (createPassword.length < 6) {
            errors.Password = "Password must be at least 6 characters.";
        }
        if (createPassword !== createConfirmPassword) {
            errors.ConfirmPassword = "Passwords do not match.";
        }
        // if (!createTeamName.trim()) errors.TeamName = "Team Name is required.";
        
        if (createSportsList.length === 0) {
            errors.Sports = "At least one sport must be added.";
        }

        setCreateError(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateUser = async (_payload: any) => {
        setCreateError(prev => ({ ...prev, General: "" }));
        // if (!validateCreateUser()) {
        //     return false;
        // }
        setIsPending(true);

        const generateRandomHexColor = () => {
            return Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
        };

        const teamPayload: TeamPayload = {
            name: createTeamName.trim(),
            code: createTeamCode.trim() ? parseInt(createTeamCode.trim(), 10) : null,
            gTeamId: 0, 
            tidyName: createTeamName.trim().toLowerCase().replace(/\s+/g, '-'),
            imgUrl: `https://preservationmaryland.org/wp-content/uploads/2017/06/flag-city-of-baltimore-maryland.png`, 
            colorCode: `#${generateRandomHexColor()}` 
        };

        if (teamPayload.code !== null && isNaN(teamPayload.code)) {
            setCreateError(prev => ({ ...prev, TeamCode: "Team code must be a number if provided.", General: "Please correct the highlighted fields."}));
            setIsPending(false);
            return false;
        }

        const finalSportsList = createSportsList.map(sport => ({
            ...sport,
            gLeagueId: Number(sport.gLeagueId) // Ensure gLeagueId is number
        }));

        const payload = {
            UserName: createUsername.trim(),
            Password: createPassword,
            Email: createEmail.trim(),
            Team: teamPayload,
            Sports: finalSportsList,
        };

        const response = await authService.UserCreate(_payload);
        setIsPending(false);

        if (response && (response.status === 201 || response.status === 200)) {
            const recordbookUploadRequired = Boolean(response?.data?.recordbook_upload_required);
            if (recordbookUploadRequired) {
                setRecordbookTeamName(response?.data?.team_name);
                setRecordbookMissing(response?.data?.recordbook_missing ?? null);
                setIsRecordbookModalOpen(true);
                return true;
            }

            alert(response.data?.message || "Account created successfully! Please log in.");
            setCreateUsername('');
            setCreateEmail('');
            setCreatePassword('');
            setCreateConfirmPassword('');
            setCreateTeamName('');
            setCreateTeamCode('');
            setCreateSportsList([]);
            setCurrentSportName('');
            setCurrentSportCode('');
            setCurrentSportGLeagueId('');
            setCurrentView('login');
        } else {
            const errorMsg = response?.data?.message || response?.data?.detail || response?.message || "Failed to create account.";
            setCreateError({ General: errorMsg });
        }
    };

    const switchToCreateUserView = () => {
        setCurrentView('register');
        setLoginError({});
    };

    const switchToLoginView = () => {
        setCurrentView('login');
        setCreateError({});
    };
    
    const switchToForgotPasswordView = () => {
        setCurrentView('forgotPassword');
        setLoginError({});
    };

    return (
        <div className="w-full min-h-screen flex flex-col justify-center items-center box-border bg-gradient-to-br from-gray-100 to-cyan-50 py-12 px-4">
            {/* Issue Report Dialog */}
            <IssueReportForm 
                open={issueDialogOpen} 
                onClose={() => setIssueDialogOpen(false)} 
            />

            <RecordbookUploadNoticeModal
                isOpen={isRecordbookModalOpen}
                teamName={recordbookTeamName}
                recordbookMissing={recordbookMissing}
                onAcknowledge={() => {
                    setIsRecordbookModalOpen(false);
                    setCreateUsername('');
                    setCreateEmail('');
                    setCreatePassword('');
                    setCreateConfirmPassword('');
                    setCreateTeamName('');
                    setCreateTeamCode('');
                    setCreateSportsList([]);
                    setCurrentSportName('');
                    setCurrentSportCode('');
                    setCurrentSportGLeagueId('');
                    setCurrentView('login');
                }}
            />

            <UploadNotificationModal
                isOpen={isUploadNotificationOpen}
                notifications={uploadNotifications}
                onAcknowledge={async () => {
                    for (const notification of uploadNotifications) {
                        try {
                            await authService.markNotificationSeen(notification.id);
                        } catch (error) {
                            console.error("Error marking notification as seen:", error);
                        }
                    }
                    setIsUploadNotificationOpen(false);
                    setUploadNotifications([]);
                }}
            />

            {currentView === 'login' && (
                <>
                    <LoginForm
                        handleLogin={handleLogin}
                        username={loginUsername}
                        setUsername={setLoginUsername}
                        showPassword={showLoginPassword}
                        password={loginPassword}
                        setPassword={setLoginPassword}
                        setShowPassword={setShowLoginPassword}
                        error={loginError}
                        setError={setLoginError as any}
                        isPending={isPending}
                        switchToCreateUser={switchToCreateUserView}
                        switchToForgotPassword={switchToForgotPasswordView}
                    />
                    <div className="mt-4">
                        <button 
                            className="text-sm text-blue-500 hover:text-blue-700 underline"
                            onClick={() => setIssueDialogOpen(true)}
                        >
                            Found an issue? Report it here
                        </button>
                    </div>
                </>
            )}
            {currentView === 'register' && (
                <CreateUserForm
                    handleCreateUser={handleCreateUser}
                    username={createUsername}
                    setUsername={setCreateUsername}
                    email={createEmail}
                    setEmail={setCreateEmail}
                    error={createError}
                    setError={setCreateError as any}
                    isPending={isPending}
                    switchToLogin={switchToLoginView}
                />
            )}
            {currentView === 'forgotPassword' && (
                <ForgotPasswordForm
                    switchToLogin={switchToLoginView}
                />
            )}
        </div>
    );
}
